import { memo, useState, useCallback, useEffect, type CSSProperties } from 'react'
import { collectionsApi } from '../../lib/api.js'
import { COLLECTION_PRESET_COLORS } from '../../lib/catalog-colors.js'
import type { Collection } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface CollectionModalProps {
  channelId:   string
  collection?: Collection
  onCreated?:  (col: Collection) => void
  onUpdated?:  (col: Collection) => void
  onClose:     () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const CollectionModal = memo(function CollectionModal({ channelId, collection, onCreated, onUpdated, onClose }: CollectionModalProps) {
  const isEdit = !!collection
  const [visible, setVisible]         = useState(false)
  const [saving,  setSaving]          = useState(false)
  const [error,   setError]           = useState<string | null>(null)
  const [name,        setName]        = useState(collection?.name        ?? '')
  const [description, setDescription] = useState(collection?.description ?? '')
  const [color,       setColor]       = useState(collection?.color ?? COLLECTION_PRESET_COLORS[0].value)

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true); setError(null)
    try {
      if (isEdit && collection) {
        const updated = await collectionsApi.update(collection.id, { name: name.trim(), description: description.trim() || undefined, color })
        onUpdated?.(updated)
      } else {
        const created = await collectionsApi.create({ channelId, name: name.trim(), description: description.trim() || undefined, color })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, isEdit, collection, name, description, color, onCreated, onUpdated])

  return (
    <ModalShell
      title={isEdit ? 'Modifier la collection' : 'Nouvelle collection'}
      size="narrow"
      visible={visible}
      onClose={handleClose}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="collection-name">Nom *</label>
            <input id="collection-name" className="tp-input" value={name} onChange={e => setName(e.target.value)} required autoFocus placeholder="Ex: Documentaires nature, Journal de 19h…" />
          </div>

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="collection-description">Description</label>
            <textarea id="collection-description" className="tp-textarea" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Description optionnelle…" />
          </div>

          <fieldset className="tp-form-field">
            <legend className="tp-field-label">Couleur</legend>
            <div className="catalog-color-options">
              {COLLECTION_PRESET_COLORS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={`catalog-color-option${color === option.value ? ' is-selected' : ''}`}
                  style={{ '--catalog-accent': option.value } as CSSProperties}
                  aria-label={option.label}
                  aria-pressed={color === option.value}
                />
              ))}
            </div>
          </fieldset>

          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !name.trim()} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})
